<template>
    <div ref="playerRef" class="flashcard-player" :class="{ 'is-fullscreen': isFullscreen }">
        <!-- Overview Screen -->
        <div v-if="screen === 'overview'" class="flashcard-overview">
            <div class="overview-hero">
                <div class="deck-icon-stack">
                    <div class="deck-card deck-card-3" />
                    <div class="deck-card deck-card-2" />
                    <div class="deck-card deck-card-1">
                        <i class="uil uil-layers" />
                    </div>
                </div>
                <h2 class="overview-title">{{ title }}</h2>
            </div>

            <div class="tw:flex tw:items-center tw:justify-center tw:gap-4 tw:mb-8">
                <div class="tw:inline-flex tw:items-center tw:gap-2">
                    <i class="uil uil-layers tw:text-lg tw:text-primary" />
                    <span class="tw:text-base tw:font-medium tw:text-gray-700">{{ cardCountLabel }}</span>
                </div>
                <span class="tw:text-gray-300 tw:text-base">&middot;</span>
                <div class="tw:inline-flex tw:items-center tw:gap-2">
                    <i class="uil uil-clock tw:text-lg tw:text-primary" />
                    <span class="tw:text-base tw:font-medium tw:text-gray-700">~{{ estimatedMinutes }} min</span>
                </div>
                <span class="tw:text-gray-300 tw:text-base">&middot;</span>
                <div class="tw:inline-flex tw:items-center tw:gap-2">
                    <i class="uil uil-brain tw:text-lg tw:text-primary" />
                    <span class="tw:text-base tw:font-medium tw:text-gray-700">Self-paced</span>
                </div>
            </div>

            <div v-if="!readOnly && cards.length" class="tw:flex tw:flex-col tw:items-center tw:mb-8">
                <e-button type="is-primary" size="is-medium" class="start-button" @click="startSession">
                    <span>Start Studying</span>
                    <span class="icon is-small"><i class="uil uil-arrow-right" /></span>
                </e-button>
            </div>

            <div v-if="showCardPreview && cards.length" class="card-preview-list">
                <div class="preview-header">
                    <p class="preview-heading">Card Preview</p>
                    <span class="preview-count">{{ cardCountLabel }}</span>
                </div>
                <div class="card-preview-scroll">
                    <div v-for="(card, index) in cards" :key="index" class="card-preview-item">
                        <span class="preview-number">{{ index + 1 }}</span>
                        <span class="preview-text">{{ card.front }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Study Session Screen -->
        <div v-else-if="screen === 'study'" class="flashcard-study">
            <div class="study-header">
                <div class="study-topbar">
                    <button class="topbar-button" @click="confirmExit">
                        <i class="uil uil-times" />
                        <span>Leave the Current Session</span>
                    </button>
                    <span class="card-counter">{{ currentIndex + 1 }} / {{ cards.length }}</span>
                    <button class="topbar-button" @click="toggleFullscreen">
                        <i class="uil" :class="isFullscreen ? 'uil-compress-arrows' : 'uil-expand-arrows-alt'" />
                        <span>{{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}</span>
                    </button>
                </div>
                <div class="progress-bar">
                    <div
                        class="progress-fill"
                        :style="{ width: progressWidth }"
                    />
                </div>
            </div>

            <div class="card-stage">
                <transition :name="slideDirection" mode="out-in">
                    <flashcard-card
                        :key="currentIndex"
                        :front="currentCard.front"
                        :back="currentCard.back"
                        :image-url="currentCard.imageUrl || null"
                        :is-flipped="isFlipped"
                        @flip="flipCard"
                    />
                </transition>
            </div>

            <!-- Recall Rating -->
            <transition name="fade-slide-up">
                <div v-if="isFlipped && showMemoryRating" class="recall-rating">
                    <p class="rating-prompt">How well did you know this?</p>
                    <div class="rating-buttons">
                        <button
                            class="rating-pill rating-danger"
                            :class="{ 'is-selected': currentResponse === 'didnt_know' }"
                            :aria-pressed="currentResponse === 'didnt_know'"
                            @click="rateCard('didnt_know')"
                        >
                            <i class="uil uil-times-circle" />
                            <span>Didn't Know</span>
                        </button>
                        <button
                            class="rating-pill rating-warning"
                            :class="{ 'is-selected': currentResponse === 'knew_some' }"
                            :aria-pressed="currentResponse === 'knew_some'"
                            @click="rateCard('knew_some')"
                        >
                            <i class="uil uil-minus-circle" />
                            <span>Knew Some</span>
                        </button>
                        <button
                            class="rating-pill rating-success"
                            :class="{ 'is-selected': currentResponse === 'knew_it' }"
                            :aria-pressed="currentResponse === 'knew_it'"
                            @click="rateCard('knew_it')"
                        >
                            <i class="uil uil-check-circle" />
                            <span>Knew It!</span>
                        </button>
                    </div>
                    <p v-if="!currentResponse" class="rating-required-hint">
                        Rate this card to continue
                    </p>
                </div>
            </transition>

            <div class="study-navigation">
                <button
                    class="nav-button"
                    :disabled="currentIndex === 0"
                    @click="previousCard"
                >
                    <i class="uil uil-arrow-left" />
                    <span>Previous</span>
                </button>
                <button
                    v-if="currentIndex < cards.length - 1"
                    class="nav-button"
                    :disabled="!canAdvance"
                    @click="nextCard"
                >
                    <span>Next</span>
                    <i class="uil uil-arrow-right" />
                </button>
                <e-button
                    v-else
                    type="is-primary"
                    :disabled="!allCardsViewed || !canAdvance"
                    @click="finishSession"
                >
                    Finish
                </e-button>
            </div>
        </div>

        <!-- Session Complete Screen -->
        <div v-else-if="screen === 'complete'" class="flashcard-complete has-text-centered">
            <div class="m-b-lg">
                <span class="icon is-large has-text-success">
                    <i class="uil uil-check-circle complete-icon" />
                </span>
                <h2 class="title is-4 m-t-sm">Session Complete!</h2>
                <p class="tw:text-base tw:text-gray-500 tw:mt-1">You reviewed {{ cardCountLabel }}</p>
            </div>

            <div class="stats-card">
                <div class="stats-row">
                    <div class="stat-item">
                        <p class="stat-value stat-accuracy">{{ sessionStats.accuracy }}%</p>
                        <p class="stat-label">Accuracy</p>
                    </div>
                    <div class="stat-item">
                        <p class="stat-value stat-correct">{{ sessionStats.correct }}</p>
                        <p class="stat-label">Correct</p>
                    </div>
                    <div class="stat-item">
                        <p class="stat-value stat-review">{{ sessionStats.needsReview }}</p>
                        <p class="stat-label">Needs Review</p>
                    </div>
                </div>
                <div class="results-bar">
                    <div
                        class="results-bar-correct"
                        :style="{ width: `${sessionStats.accuracy}%` }"
                    />
                    <div
                        class="results-bar-review"
                        :style="{ width: `${100 - sessionStats.accuracy}%` }"
                    />
                </div>
            </div>

            <div class="complete-actions">
                <e-button type="is-light" @click="exitComplete">
                    Back to Overview
                </e-button>
                <e-button type="is-primary" @click="studyAgain">
                    <span class="icon is-small"><i class="uil uil-refresh" /></span>
                    <span>Study Again</span>
                </e-button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import FlashcardCard from './FlashcardCard.vue';

const props = defineProps({
    cards: {
        type: Array,
        required: true,
    },
    title: {
        type: String,
        default: 'Flashcards',
    },
    showMemoryRating: {
        type: Boolean,
        default: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    sessionSize: {
        type: Number,
        default: null,
    },
    totalDeckCards: {
        type: Number,
        default: 0,
    },
    showCardPreview: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['complete', 'back', 'study-again']);

const playerRef = ref(null);
const screen = ref('overview');
const currentIndex = ref(0);
const isFlipped = ref(false);
const viewedCards = ref(new Set());
const cardResponses = reactive({});
const sessionNumber = ref(1);
const slideDirection = ref('slide-left');
const isFullscreen = ref(false);

const currentCard = computed(() => {
    return props.cards[currentIndex.value] || { id: null, front: '', back: '' };
});

const currentResponse = computed(() => {
    return cardResponses[currentIndex.value] || null;
});

const allCardsViewed = computed(() => {
    return viewedCards.value.size >= props.cards.length;
});

const canAdvance = computed(() => {
    if (props.showMemoryRating) {
        return !!currentResponse.value;
    }
    return isFlipped.value;
});

const isSessionSubset = computed(() => {
    return props.totalDeckCards > 0 && props.cards.length < props.totalDeckCards;
});

const uniqueCardCount = computed(() => {
    const ids = new Set(props.cards.map(c => c.id));
    return ids.size;
});

const hasRepeats = computed(() => {
    return uniqueCardCount.value < props.cards.length;
});

const cardCountLabel = computed(() => {
    const total = props.cards.length;
    const unique = uniqueCardCount.value;

    if (hasRepeats.value) {
        if (isSessionSubset.value) {
            return `${total} cards (${unique} unique of ${props.totalDeckCards})`;
        }
        return `${total} cards (${unique} unique)`;
    }

    if (isSessionSubset.value) {
        return `${total} cards`;
    }
    return `${total} cards`;
});

const estimatedMinutes = computed(() => {
    const secondsPerCard = 15;
    return Math.max(1, Math.round((props.cards.length * secondsPerCard) / 60));
});

const progressWidth = computed(() => {
    if (!props.cards.length) return '0%';
    return `${((currentIndex.value + 1) / props.cards.length) * 100}%`;
});

const sessionStats = computed(() => {
    let correct = 0;
    let needsReview = 0;

    props.cards.forEach((card, index) => {
        const confidence = cardResponses[index];
        if (!confidence) return; // Skip unrated cards
        if (confidence === 'knew_it' || confidence === 'knew_some') {
            correct++;
        } else {
            needsReview++;
        }
    });

    const total = correct + needsReview;
    const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

    return { correct, needsReview, accuracy };
});

const submissionPayload = computed(() => {
    const stats = sessionStats.value;

    return {
        cards_viewed: viewedCards.value.size,
        total_correct: stats.correct,
        total_needs_review: stats.needsReview,
        accuracy_percentage: stats.accuracy,
        session_number: sessionNumber.value,
        card_responses: props.cards
            .map((card, index) => ({ card, index }))
            .filter(({ index }) => cardResponses[index])
            .map(({ card, index }) => ({
                flashcard_card_id: card.id,
                confidence: cardResponses[index],
            })),
    };
});

// ── Fullscreen ──
// Uses CSS-based fullscreen (position:fixed) instead of the browser Fullscreen
// API so that PrimeVue Image preview overlays (teleported to <body>) remain
// visible above the player.

function enterFullscreen() {
    isFullscreen.value = true;
    document.body.classList.add('flashcard-fullscreen-active');
    document.body.style.overflow = 'hidden';
}

function exitFullscreen() {
    isFullscreen.value = false;
    document.body.classList.remove('flashcard-fullscreen-active');
    document.body.style.overflow = '';
}

function toggleFullscreen() {
    if (isFullscreen.value) {
        exitFullscreen();
    } else {
        enterFullscreen();
    }
}

// ── Session Actions ──

function markCurrentCardViewed() {
    viewedCards.value.add(currentIndex.value);
}

function resetSessionProgress() {
    currentIndex.value = 0;
    isFlipped.value = false;
    viewedCards.value = new Set();
    Object.keys(cardResponses).forEach(key => delete cardResponses[key]);
}

function startSession() {
    screen.value = 'study';
    resetSessionProgress();
    markCurrentCardViewed();
}

function flipCard() {
    isFlipped.value = !isFlipped.value;
}

function rateCard(confidence) {
    cardResponses[currentIndex.value] = confidence;
}

function nextCard() {
    if (currentIndex.value < props.cards.length - 1) {
        slideDirection.value = 'slide-left';
        currentIndex.value++;
        isFlipped.value = false;
        markCurrentCardViewed();
    }
}

function previousCard() {
    if (currentIndex.value > 0) {
        slideDirection.value = 'slide-right';
        currentIndex.value--;
        isFlipped.value = false;
    }
}

function handleKeydown(event) {
    if (screen.value !== 'study') return;
    switch (event.key) {
        case 'ArrowLeft':
            event.preventDefault();
            previousCard();
            break;
        case 'ArrowRight':
            event.preventDefault();
            if (currentIndex.value < props.cards.length - 1 && canAdvance.value) {
                nextCard();
            }
            break;
        case ' ':
        case 'Spacebar':
            event.preventDefault();
            flipCard();
            break;
        default:
            break;
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
    if (isFullscreen.value) {
        exitFullscreen();
    }
    document.body.classList.remove('flashcard-fullscreen-active');
});

function finishSession() {
    screen.value = 'complete';
    emit('complete', submissionPayload.value);
}

function studyAgain() {
    sessionNumber.value++;
    resetSessionProgress();
    screen.value = 'study';
    markCurrentCardViewed();
    emit('study-again');
}

function confirmExit() {
    if (isFullscreen.value) {
        exitFullscreen();
    }
    screen.value = 'overview';
    isFlipped.value = false;
}

function exitComplete() {
    if (isFullscreen.value) {
        exitFullscreen();
    }
    screen.value = 'overview';
    emit('back');
}
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';

.flashcard-player {
    max-width: 700px;
    margin: 0 auto;
    padding: 1.5rem 1rem;

    // ── Fullscreen Mode ──
    // CSS-based fullscreen so PrimeVue Image preview overlays remain visible.
    &.is-fullscreen {
        position: fixed;
        inset: 0;
        z-index: 10000;
        max-width: 100%;
        height: 100vh;
        padding: 2rem 2rem 1.5rem;
        background: var(--scheme-main, $white);
        display: flex;
        flex-direction: column;
        overflow-y: auto;

        .flashcard-study {
            max-width: 800px;
            width: 100%;
            margin: 0 auto;
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        .card-stage {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 400px;

            // Let the card fill the available width within the stage
            > * {
                width: 100%;
            }
        }

        .flashcard-complete {
            max-width: 700px;
            width: 100%;
            margin: auto;
        }

        .study-navigation {
            margin-top: auto;
            padding-top: 1.5rem;
        }

        .recall-rating {
            max-width: 800px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
        }

        .topbar-button {
            background: var(--scheme-main, $white);
            border-color: var(--border, $grey-lighter);
            color: var(--text-strong, $grey-dark);

            &:hover {
                background: var(--background, $grey-lighter);
                border-color: var(--border-hover, $grey-light);
                color: var(--text-strong, $black);
            }
        }

        .card-counter {
            color: var(--text, $grey);
        }

        .nav-button {
            color: var(--text, $grey);
            border-color: var(--border, $grey-lighter);

            &:hover:not(:disabled) {
                color: var(--text-strong, $black);
                border-color: var(--border-hover, $grey-light);
            }
        }
    }
}

// ── Overview Screen ──

.flashcard-overview {
    animation: fadeIn 0.3s ease;
}

.overview-hero {
    text-align: center;
    padding: 2rem 0 0.75rem;
}

.deck-icon-stack {
    position: relative;
    width: 80px;
    height: 80px;
    margin: 0 auto 1.25rem;
}

.deck-card {
    position: absolute;
    border-radius: 10px;
    border: 1px solid $grey-lighter;

    &.deck-card-3 {
        width: 56px;
        height: 68px;
        background: darken($grey-lightest, 2%);
        top: 0;
        left: 50%;
        transform: translateX(-50%) rotate(6deg);
    }

    &.deck-card-2 {
        width: 56px;
        height: 68px;
        background: $grey-lightest;
        top: 3px;
        left: 50%;
        transform: translateX(-50%) rotate(3deg);
    }

    &.deck-card-1 {
        width: 56px;
        height: 68px;
        background: $white;
        top: 6px;
        left: 50%;
        transform: translateX(-50%) rotate(0deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(36, 43, 54, 0.08);

        i {
            font-size: 1.5rem;
            color: $primary;
        }
    }
}

.overview-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: $black;
    margin-bottom: 0.25rem;
}

.start-button {
    border-radius: 100px;
    padding-left: 2rem;
    padding-right: 1.75rem;
    font-weight: 500;
    min-width: 220px;

    .icon {
        margin-left: 0.25rem;
    }
}

.preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.preview-heading {
    font-size: 0.85rem;
    font-weight: 500;
    color: $grey;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.preview-count {
    font-size: 0.8rem;
    color: $grey-light;
}

.card-preview-scroll {
    max-height: 250px;
    overflow-y: auto;
    background: $white-ter;
    border-radius: $radius-large;
    padding: 0.5rem;
}

.card-preview-item {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    border-radius: $radius;
    transition: background 0.15s ease;

    &:hover {
        background: rgba($primary, 0.04);
    }
}

.preview-number {
    color: $primary;
    font-weight: 600;
    font-size: 0.85rem;
    min-width: 1.5rem;
    text-align: right;
}

.preview-text {
    color: $black;
    line-height: 1.4;
}

// ── Study Screen ──

.study-header {
    margin-bottom: 1.25rem;
    position: relative;
    z-index: 1;
}

.study-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.topbar-button {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.35rem 0.75rem;
    border: 1px solid $grey-lighter;
    border-radius: 100px;
    background: $white;
    color: $grey-dark;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    i {
        font-size: 1.1rem;
    }

    &:hover {
        background: $grey-lighter;
        color: $black;
        border-color: $grey-light;
    }
}

.card-counter {
    color: $grey;
    font-weight: 600;
    font-size: 0.9rem;
}

.progress-bar {
    width: 100%;
    height: 4px;
    background: $grey-lighter;
    border-radius: 2px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: $primary;
    border-radius: 2px;
    transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-stage {
    position: relative;
    min-height: 320px;
    // Card faces are position:absolute so they don't push flow height.
    // Pad bottom to prevent overlap with recall-rating / navigation below.
    padding-bottom: 2rem;
    margin-bottom: 1rem;
}

// ── Recall Rating ──

.recall-rating {
    position: relative;
    z-index: 1;
    padding: 1.25rem;
    background: $white-ter;
    border-radius: 12px;
    text-align: center;
    margin-top: 1rem;
}

.rating-prompt {
    font-weight: 500;
    color: $grey;
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
}

.rating-buttons {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.rating-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 1rem;
    border: 1px solid $grey-lighter;
    border-radius: 100px;
    background: $white;
    color: $grey;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    i {
        font-size: 1.1rem;
    }

    &:hover {
        border-color: $grey-light;
    }

    &.rating-danger {
        border-left: 3px solid $danger;

        &.is-selected {
            background: $danger;
            border-color: $danger;
            color: $white;
            transform: scale(1.02);
        }
    }

    &.rating-warning {
        border-left: 3px solid $warning;

        &.is-selected {
            background: $warning;
            border-color: $warning;
            color: $black;
            transform: scale(1.02);
        }
    }

    &.rating-success {
        border-left: 3px solid $success;

        &.is-selected {
            background: $success;
            border-color: $success;
            color: $white;
            transform: scale(1.02);
        }
    }
}

.rating-required-hint {
    font-size: 0.8rem;
    color: $grey-light;
    margin-top: 0.5rem;
}

// ── Navigation ──

.study-navigation {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 1.5rem;
}

.nav-button {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid $grey-lighter;
    border-radius: $radius-large;
    background: transparent;
    color: $grey;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
        font-size: 1.2rem;
    }

    &:hover:not(:disabled) {
        border-color: $grey-light;
        color: $black;
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
}

.complete-icon {
    font-size: 3rem;
}

// ── Complete Screen ──

.stats-card {
    background: $white-ter;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
}

.stats-row {
    display: flex;
    justify-content: space-around;
    margin-bottom: 1.25rem;
}

.stat-item {
    text-align: center;
}

.stat-value {
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 0.25rem;
}

.stat-accuracy {
    color: $primary;
}

.stat-correct {
    color: $success;
}

.stat-review {
    color: $danger;
}

.stat-label {
    font-size: 0.8rem;
    color: $grey;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.results-bar {
    display: flex;
    height: 6px;
    border-radius: 3px;
    overflow: hidden;
    gap: 2px;
}

.results-bar-correct {
    background: $success;
    border-radius: 3px 0 0 3px;
    transition: width 0.5s ease;
}

.results-bar-review {
    background: $danger;
    border-radius: 0 3px 3px 0;
    transition: width 0.5s ease;
}

.complete-actions {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
}

// ── Transitions ──

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
    transition: all 0.25s ease-out;
}

.slide-left-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-left-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-right-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-right-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.fade-slide-up-enter-active {
    transition: all 0.3s ease-out;
}

.fade-slide-up-leave-active {
    transition: all 0.2s ease-in;
}

.fade-slide-up-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.fade-slide-up-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 768px) {
    .study-topbar {
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .card-counter {
        order: 3;
        width: 100%;
        text-align: center;
    }

    .topbar-button span {
        display: none;
    }
}
</style>

<!-- Global (unscoped) style: elevate PrimeVue Image preview above the CSS-based fullscreen player -->
<style lang="scss">
body.flashcard-fullscreen-active .p-image-mask {
    z-index: 10001 !important;
}
</style>
